import { z } from 'zod';
import { BgmSchema } from './invitation.js';
import { THANKYOU_BASE_QUOTA } from './quota.js';
/** @deprecated 감사장 기본 생성 한도는 THANKYOU_BASE_QUOTA. 기존 import 호환용 alias */
export const MAX_THANKYOU_CARDS = THANKYOU_BASE_QUOTA;
/** 디자인 HTML 최대 크기 (200KB) */
export const THANKYOU_DESIGN_HTML_MAX = 200_000;
/** 임포트/업로드된 디자인 에셋 — 에디터 에셋 패널 데이터 */
export const ThankYouDesignAssetSchema = z.object({
    originalPath: z.string(),
    s3Url: z.string(),
    category: z.enum(['image', 'font']),
});
/** 디자인(템플릿) — admin 저작 HTML(Liquid) 문서 */
export const ThankYouDesignSchema = z.object({
    id: z.string(),
    name: z.string(),
    status: z.enum(['ACTIVE', 'INACTIVE']),
    sortOrder: z.number().int(),
    html: z.string(),
    assets: z.array(ThankYouDesignAssetSchema).default([]),
    thumbnailUrl: z.string().nullable().optional(),
    aspectRatio: z.string(),
});
/** 카드 내용 (data jsonb) */
export const ThankYouCardDataSchema = z.object({
    /** 감사장 이름 — 목록·에디터 헤더에서 카드를 식별하는 표시명. 템플릿 렌더링과 무관. 비면 '새 감사장' */
    title: z.string().max(60).optional(),
    /** 넘버링 사진 슬롯 — index i ↔ 디자인의 {{ photo{i+1} }} */
    photos: z.array(z.string()).default([]),
    /** @deprecated 구 카드 읽기 호환용 — photos가 비면 [photoUrl]로 정규화. 신규 카드는 photos만 채운다 */
    photoUrl: z.string().optional(),
    message: z.string().max(800).default(''),
    /** 보내는 이 1·2 — 디자인의 {{ name1 }}/{{ name2 }}에 대응. 한쪽만 채우면 그 하나만 표시 */
    name1: z.string().max(120).optional(),
    name2: z.string().max(120).optional(),
    /** @deprecated 구 카드 읽기 호환용 — name1/name2가 비면 이 값으로 {{ names }} 폴백. 신규 카드는 name1/name2만 채운다 */
    names: z.string().max(120).default(''),
    weddingDate: z.string().optional(),
    bgm: BgmSchema.optional(),
    /** 카카오톡 공유(OG) 메타. 비면 buildThankYouOg가 자동값으로 폴백. 청첩장 ShareSchema 기반이되 에디터 입력 길이에 맞춰 max 제약 추가 */
    share: z
        .object({
        ogTitle: z.string().max(60).optional(),
        ogDescription: z.string().max(200).optional(),
        ogImageUrl: z.string().optional(),
    })
        .optional(),
});
/**
 * 디자인 HTML에서 가장 큰 photoN 번호를 사진 슬롯 개수로 산출. 없으면 0.
 * `{{ photo3 }}`·`{% if photo3 %}` 모두 매칭(photo + 숫자 경계). `{{ photoUrl }}`은 숫자가 없어 미매칭.
 */
export function countPhotoSlots(html) {
    let max = 0;
    for (const m of html.matchAll(/\bphoto(\d+)\b/g)) {
        const n = Number(m[1]);
        if (n > max)
            max = n;
    }
    return max;
}
/**
 * 카드 데이터의 사진 배열을 정규화한다.
 * photos가 있으면 그대로, 비어 있고 구 photoUrl만 있으면 [photoUrl]로 승격.
 */
export function normalizeThankYouPhotos(data) {
    if (data.photos?.length)
        return data.photos;
    return data.photoUrl ? [data.photoUrl] : [];
}
/**
 * 보내는 이를 {{ names }}용 합성 문자열로 만든다.
 * name1/name2 중 채워진 것만 ' · '로 결합(한쪽만 있으면 그 하나만), 둘 다 비면 구 names로 폴백.
 */
export function composeThankYouNames(data) {
    const parts = [data.name1, data.name2].filter((s) => !!s && s.trim().length > 0);
    if (parts.length)
        return parts.join(' · ');
    return data.names ?? '';
}
/**
 * 감사장 카카오톡 공유(OG) 값 — 공유 모달·OG 라우트 공용 단일 진실 출처.
 * share.* 가 채워져 있으면 그 값을, 비면 자동값으로 폴백한다.
 *   title:       share.ogTitle       || `{보내는이} 감사 인사` || '감사장'
 *   description: share.ogDescription  || '저희 결혼을 축하해 주셔서 감사합니다'
 *   image:       share.ogImageUrl     || 대표 사진 첫 장 || ''
 */
export function buildThankYouOg(data) {
    const senderNames = composeThankYouNames(data);
    const title = data.share?.ogTitle?.trim() || (senderNames ? `${senderNames} 감사 인사` : '감사장');
    const description = data.share?.ogDescription?.trim() || '저희 결혼을 축하해 주셔서 감사합니다';
    const image = data.share?.ogImageUrl?.trim() || normalizeThankYouPhotos(data)[0] || '';
    return { title, description, image };
}
/** 목록·에디터 헤더용 카드 표시명. title이 비면 기본값('새 감사장')으로 폴백. */
export function thankYouCardTitle(data) {
    return data?.title?.trim() || '새 감사장';
}
/** 카드 인스턴스 */
export const ThankYouCardSchema = z.object({
    id: z.string(),
    userId: z.string(),
    designId: z.string(),
    slug: z.string(),
    status: z.enum(['DRAFT', 'PUBLISHED']),
    data: ThankYouCardDataSchema,
    sourceInvitationId: z.string().nullable().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    publishedAt: z.string().datetime().nullable().optional(),
    // 만료일 — 비활성 항목은 발행 +7일, 활성(활성화된) 항목은 null(영구)
    expiresAt: z.string().datetime().nullable().optional(),
    // 항목별 활성화 — true면 워터마크 제거 + 영구화(expiresAt=null)
    activated: z.boolean().optional().default(false),
});
/** 목록 응답용 — 카드 + 참조 디자인(디자인이 삭제/비활성이어도 미리보기에 필요, 없으면 null) */
export const ThankYouCardWithDesignSchema = ThankYouCardSchema.extend({
    design: ThankYouDesignSchema.nullable(),
});
// ── 요청 스키마 ──
export const CreateThankYouCardSchema = z.object({
    designId: z.string().optional(),
    fromInvitationId: z.string().optional(),
});
/** /thanks/* 라우트와 충돌하는 예약어 — 공개 slug로 사용 불가 */
const RESERVED_THANKYOU_SLUGS = ['templates', 'edit', 'new', 'list'];
/** 공개 URL slug — 소문자/숫자/하이픈, 3~50자, 예약어 제외 */
export const ThankYouSlugSchema = z
    .string()
    .regex(/^[a-z0-9-]{3,50}$/, '주소는 소문자·숫자·하이픈 3~50자만 사용할 수 있습니다')
    .refine((s) => !RESERVED_THANKYOU_SLUGS.includes(s), '사용할 수 없는 주소입니다');
export const UpdateThankYouCardSchema = z.object({
    designId: z.string().optional(),
    data: ThankYouCardDataSchema.partial().optional(),
    slug: ThankYouSlugSchema.optional(),
});
export const CreateThankYouDesignSchema = z.object({
    name: z.string().min(1),
    status: z.enum(['ACTIVE', 'INACTIVE']).default('ACTIVE'),
    sortOrder: z.number().int().default(0),
    html: z.string().min(1).max(THANKYOU_DESIGN_HTML_MAX),
    assets: z.array(ThankYouDesignAssetSchema).optional(),
    thumbnailUrl: z.string().nullable().optional(),
    aspectRatio: z.string().default('939/1676'),
});
export const UpdateThankYouDesignSchema = CreateThankYouDesignSchema.partial();
//# sourceMappingURL=thankyou.js.map